/** A session read that hangs is worse than one that fails — bound it. */
const DEFAULT_TIMEOUT_MS = 10_000;

/** The subset of Kratos identity traits the apps use before GraphQL has loaded. */
export interface SessionTraits {
  email?: string;
  name?: { first?: string; last?: string };
}

/** What the app is told when the session is gone. */
export interface SessionExpiry {
  /**
   * False when the very first read already found no session. Kratos answers 401
   * both for "no cookie at all" and "your session expired", so without this a
   * first-time visitor would be told a session of theirs had ended.
   */
  hadActiveSession: boolean;
}

export type SessionStatus =
  | { state: 'active'; identityId: string; traits?: SessionTraits }
  | { state: 'expired' }
  | { state: 'unknown' };

export type SessionService = ReturnType<typeof createSessionService>;

export interface SessionServiceOptions {
  kratosEndpoint: string;
  fetchFn?: typeof fetch;
  timeoutMs?: number;
}

export function createSessionService({
  kratosEndpoint,
  fetchFn = fetch,
  timeoutMs = DEFAULT_TIMEOUT_MS,
}: SessionServiceOptions) {
  let cachedIdentityId: string | undefined;
  let inFlight: Promise<SessionStatus> | undefined;
  let expiryAnnounced = false;
  let hadActiveSession = false;
  const expiredListeners = new Set<(expiry: SessionExpiry) => void>();

  /**
   * A listener dispatches into the app (an XState event), which can throw. Left
   * unguarded that would reject `revalidate()` and the boot path would report
   * "Kratos unreachable" for a session Kratos had actually rejected.
   */
  function notifyExpired(listener: (expiry: SessionExpiry) => void): void {
    try {
      listener({ hadActiveSession });
    } catch (error) {
      console.error('Session expiry listener failed:', error);
    }
  }

  /** Single place where a fresh answer from Kratos updates what we believe. */
  function apply(status: SessionStatus): SessionStatus {
    if (status.state === 'active') {
      cachedIdentityId = status.identityId;
      hadActiveSession = true;
      expiryAnnounced = false;

      return status;
    }

    if (status.state === 'expired') {
      cachedIdentityId = undefined;

      // Every in-flight operation discovers the expiry; announcing it once keeps
      // the app from starting a Kratos logout flow per failed operation.
      if (!expiryAnnounced) {
        expiryAnnounced = true;
        expiredListeners.forEach(notifyExpired);
      }
    }

    return status;
  }

  async function fetchStatus(): Promise<SessionStatus> {
    if (!kratosEndpoint) return apply({ state: 'unknown' });

    let response: Response;
    // A request that never answers would never clear `inFlight`, so every later
    // caller — including urql's `refreshAuth`, which blocks its queue — would
    // attach to the same hung promise and the app would never leave its loader.
    const abort = new AbortController();
    const timeout = setTimeout(() => abort.abort(), timeoutMs);

    try {
      response = await fetchFn(`${kratosEndpoint}/sessions/whoami`, {
        method: 'GET',
        credentials: 'include',
        headers: { accept: 'application/json' },
        signal: abort.signal,
      });
    } catch {
      // Offline, DNS, CORS, timeout: we learned nothing about the session.
      // Saying "expired" here would sign out a user whose session is fine.
      return apply({ state: 'unknown' });
    } finally {
      clearTimeout(timeout);
    }

    // Kratos answers 401 with no credentials and 403 when the session cannot be
    // used as-is (e.g. a higher AAL is required) — both need a fresh login.
    if (response.status === 401 || response.status === 403) {
      return apply({ state: 'expired' });
    }

    if (!response.ok) return apply({ state: 'unknown' });

    let session: { identity?: { id?: string; traits?: SessionTraits } };

    try {
      session = (await response.json()) as typeof session;
    } catch {
      return apply({ state: 'unknown' });
    }

    const { identity } = session;

    return apply(
      identity?.id
        ? { state: 'active', identityId: identity.id, traits: identity.traits }
        : { state: 'unknown' }
    );
  }

  /**
   * One request per burst: at boot the app, the urql auth exchange and the
   * visibility watchdog all want the same answer at the same moment.
   */
  function revalidate(): Promise<SessionStatus> {
    if (!inFlight) {
      inFlight = fetchStatus().finally(() => {
        inFlight = undefined;
      });
    }

    return inFlight;
  }

  async function getIdentityId(): Promise<string | undefined> {
    if (cachedIdentityId) return cachedIdentityId;

    await revalidate();

    return cachedIdentityId;
  }

  /** Subscribe to "the session is gone"; the app decides what that means (sign out). */
  function onExpired(listener: (expiry: SessionExpiry) => void): () => void {
    expiredListeners.add(listener);

    // The expiry is a latched fact, not a passing edge: whether the boot read or
    // the urql auth exchange gets there first is an import-order accident, so a
    // subscriber that arrives late still has to act on it.
    if (expiryAnnounced) notifyExpired(listener);

    return () => expiredListeners.delete(listener);
  }

  /** The id we already know, for callers that cannot await (urql header building). */
  function identityId(): string | undefined {
    return cachedIdentityId;
  }

  return { getIdentityId, identityId, onExpired, revalidate };
}
